import React, { useCallback } from 'react';

import { Description, Error, UploadFile } from '@mui/icons-material';
import {
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import {
  CSV_CONSTRAINTS,
  CSVValidationResult,
  getSuccessMessage,
  processFileRejectionError,
  shouldDisplayErrorInField,
  shouldDisplayErrorInList,
  validateCSV,
} from 'src/pages/Monitor/validation';

type CSVUploadSectionProps = {
  document: File | null;
  setDocument: (file: File | null) => void;
  csvValidation: CSVValidationResult | null;
  setCsvValidation: (validation: CSVValidationResult | null) => void;
  isValidating: boolean;
  setIsValidating: (validating: boolean) => void;
};

export function CSVUploadSection({
  document,
  setDocument,
  csvValidation,
  setCsvValidation,
  isValidating,
  setIsValidating,
}: CSVUploadSectionProps) {
  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: any) => {
      if (acceptedFiles.length) {
        const file = acceptedFiles[0];
        setDocument(file);
        setCsvValidation(null);
        setIsValidating(true);

        try {
          const validation = await validateCSV(file);
          setCsvValidation(validation);
        } catch (error) {
          setCsvValidation({
            isValid: false,
            errors: ['Erro ao validar arquivo'],
          });
        } finally {
          setIsValidating(false);
        }
      }

      if (rejectedFiles.length) {
        const error = rejectedFiles[0].errors[0];
        const validationError = processFileRejectionError(error);

        setCsvValidation({
          isValid: false,
          errors: [validationError.message],
        });
      }
    },
    [setDocument, setCsvValidation, setIsValidating],
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'text/csv': ['.csv'],
    },
    onDrop,
    maxFiles: 1,
    maxSize: CSV_CONSTRAINTS.MAX_FILE_SIZE,
    noDragEventsBubbling: true,
  });

  const hasCsvError = csvValidation && !csvValidation.isValid;

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
        Arraste e solte sua planilha no espaço abaixo para importar
        automaticamente os investimentos da carteira. Você também pode clicar na
        área destacada para selecionar o arquivo do seu computador.
      </Typography>

      <Box
        {...getRootProps()}
        onDragOver={(e) => {
          e.currentTarget.style.border = hasCsvError
            ? '2px dashed #d32f2f'
            : '2px dashed #1976D2';
          e.currentTarget.style.backgroundColor = hasCsvError
            ? '#ffebee'
            : '#e7eff5';
          e.currentTarget.style.transition = 'all 0.3s ease';
        }}
        onDragLeave={(e) => {
          e.currentTarget.style.border = hasCsvError
            ? '2px dashed #d32f2f'
            : '2px dashed #ccc';
          e.currentTarget.style.backgroundColor = hasCsvError
            ? '#ffebee'
            : 'transparent';
          e.currentTarget.style.transition = 'all 0.3s ease';
        }}
        sx={{
          width: '100%',
          minHeight: 100,
          border: hasCsvError
            ? '2px dashed #d32f2f'
            : document
              ? '2px dashed #1976D2'
              : '2px dashed #ccc',
          borderRadius: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          backgroundColor: hasCsvError
            ? '#ffebee'
            : document
              ? '#e7eff5'
              : 'transparent',
          '&:hover': {
            borderColor: hasCsvError ? '#d32f2f' : '#1976D2',
            backgroundColor: hasCsvError ? '#ffebee' : '#e7eff5',
          },
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            py: 6,
          }}>
          <input {...getInputProps()} />
          {isValidating ? (
            <>
              <Typography variant="body2" sx={{ textAlign: 'center', px: 1 }}>
                Validando arquivo...
              </Typography>
            </>
          ) : document ? (
            <>
              {hasCsvError ? (
                <Error fontSize="large" htmlColor="#d32f2f" />
              ) : (
                <Description fontSize="large" htmlColor="#1976D2" />
              )}
              <Typography variant="body2" sx={{ textAlign: 'center', px: 1 }}>
                {document.name}
              </Typography>
              {csvValidation?.isValid && csvValidation.data && (
                <Typography
                  variant="caption"
                  sx={{ color: 'success.main', mt: 1 }}>
                  {getSuccessMessage(csvValidation.data)}
                </Typography>
              )}

              {csvValidation && shouldDisplayErrorInField(csvValidation) && (
                <Typography
                  variant="caption"
                  sx={{ color: '#d32f2f', mt: 1, textAlign: 'center' }}>
                  {csvValidation.errors[0]}
                </Typography>
              )}
            </>
          ) : (
            <>
              <UploadFile
                fontSize="large"
                htmlColor={hasCsvError ? '#d32f2f' : '#1976D2'}
              />
              <Typography variant="body1" sx={{ textAlign: 'center' }}>
                <Typography
                  sx={{
                    textDecoration: 'none',
                    fontWeight: 600,
                    color: hasCsvError ? '#d32f2f' : '#1976D2',
                  }}>
                  Escolha um arquivo
                </Typography>
                &nbsp;ou arraste e solte aqui
              </Typography>

              {csvValidation && shouldDisplayErrorInField(csvValidation) && (
                <Typography
                  variant="caption"
                  sx={{ color: '#d32f2f', mt: 1, textAlign: 'center' }}>
                  {csvValidation.errors[0]}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>

      {csvValidation && shouldDisplayErrorInList(csvValidation) && (
        <Box
          sx={{
            mt: 2,
            width: '100%',
            backgroundColor: '#ffebee',
            p: 2,
            maxHeight: '300px',
            overflowY: 'scroll',
            border: '1px solid #f07e7e',
            borderRadius: '4px',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#f07e7e',
              height: '30px',
              borderRadius: '4px',
              px: 2,
            },
          }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
            Erros encontrados no arquivo CSV:
          </Typography>
          <TableContainer>
            <Table size="small" padding="none" aria-label="tabela de erros CSV">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      fontSize: 13,
                      background: '#ffeaea',
                      borderBottom: '1px solid #f07e7e',
                    }}>
                    Mensagem de erro
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {csvValidation.errors.map((error, index) => (
                  <TableRow key={index}>
                    <TableCell
                      sx={{
                        fontSize: 12,
                        color: '#b71c1c',
                        background: index % 2 === 0 ? '#fff6f6' : '#ffeaea',
                        borderBottom: '1px solid #f7bcbc',
                        py: 0.5,
                      }}>
                      {error}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {csvValidation &&
        !csvValidation.isValid &&
        !shouldDisplayErrorInField(csvValidation) &&
        csvValidation.errors.length > 0 && (
          <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
            {csvValidation.errors[0]}
          </Alert>
        )}

      <Typography variant="body1" sx={{ my: 4, textAlign: 'center' }}>
        Para baixar a planilha modelo que deve ser usada para importar os
        dados&nbsp;
        <Typography
          component="a"
          href="/exemplo_carteira.csv"
          download="exemplo_carteira.csv"
          color="#1976D2"
          sx={{ textDecoration: 'none' }}>
          clique aqui
        </Typography>
      </Typography>
    </Box>
  );
}
